import { PublicClientApplication } from '@azure/msal-browser'
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser'
import msalConfig from '@/services/agents/msal/msalConfig.js'
import { AuthenticationError, PopupError, TokenError } from '@/config/authConfig.js'

class MsalAgent {
  constructor() {
    // authentication client
    this.msalClient = null
    this.scopes = ['openid', 'profile', 'email']

    // authentication state
    this.account = null
    this.authenticated = false

    // tokens (all are JWT-based)
    this.accessToken = null // from IdP (to call /userinfo endpoint)
    this.idToken = null // from IdP
  }

  async initialize(options = {}) {
    const authority = options.domain
      ? `${options.domain}/${options.tenant}`
      : msalConfig.auth.authority
    console.debug('[msalAgent]: authority=', authority)

    const auth = {
      ...msalConfig.auth,
      ...options,
      authority: authority
    }

    const config = {
      ...msalConfig,
      auth: auth
    }

    console.debug('[msalAgent]: config=', config)

    this.msalClient = new PublicClientApplication(config)
    await this.msalClient.initialize()
  }

  /*
   * Response Handling Methods
   */

  async handleRedirect() {
    try {
      const hash = window.location.hash

      if (hash.startsWith('#state') || hash.startsWith('#code')) {
        const redirectResponse = await this.msalClient.handleRedirectPromise()
        return this.processLoginResponse(redirectResponse)
      } else if (hash.startsWith('#error')) {
        const hashString = hash.split('=')
        throw new AuthenticationError(hashString[1] || 'unknown')
      } else {
        // potentially a logout or token redirect occurred
        await this.msalClient.handleRedirectPromise()
        return null
      }
    } catch (error) {
      console.error('[msalAgent]: handleRedirect error.', error)
      this.authenticated = false
      this.setAccount(null)
      throw error
    }
  }

  processLoginResponse(response) {
    // Response
    // https://azuread.github.io/microsoft-authentication-library-for-js/ref/modules/_azure_msal_common.html#authenticationresult
    console.debug('[msalAgent]: post login response=', response)
    if (response) {
      // initialize account and tokens
      this.setAccount(response.account)
      this.accessToken = response.accessToken
      this.idToken = response.idToken
      this.authenticated = true

      return {
        account: response.account,
        state: response.state ? JSON.parse(decodeURIComponent(response.state)) : {}
      }
    } else {
      return {}
    }
  }

  async restoreSession() {
    try {
      // rather than forcing a login flow, check for existing sessions in the cache
      console.debug('[msalAgent]: Checking for active account in session.')
      const account = this.findAccount()
      console.debug('[msalAgent]: Account=', account)
      account ? this.setAccount(account) : this.setAccount(null)
      this.authenticated = !!account
    } catch (e) {
      console.error('[msalAgent]: restoreSession error.', e)
    }
  }

  /*
   * Login/Logout Methods
   */

  constructLoginRequest(options) {
    const domainHint = options?.domainHint || ''
    const loginHint = options?.loginHint || ''
    const state = options?.state || {}
    return {
      prompt: options?.prompt || 'select_account',
      domainHint: domainHint,
      loginHint: loginHint,
      scopes: options?.scopes || this.scopes,
      state: encodeURIComponent(JSON.stringify(state)),
      // passed directly onto /authorize URL
      extraQueryParameters: {
        domain_hint: domainHint,
        login_hint: loginHint,
        ui_locales: options?.locale,
        ...options?.extraQueryParameters
      }
      // override only used if navigateToLoginRequestUrl is true...
      // redirectStartPage: '/'
      // callback passed in the target URL; returning false stops the navigation...
      // onRedirectNavigate: this.redirectNavigation(targetURL)
    }
  }

  async loginWithPopup(options) {
    const loginRequest = {
      ...this.constructLoginRequest(options)
    }
    // popupErrorCodes = ['popup_window_error', 'empty_window_error', 'interaction_in_progress']

    try {
      // clear out msal session items in case there was a previous fa
      // this.msalClient.browserStorage?.clear()

      // show login popup
      const loginResponse = await this.msalClient.loginPopup(loginRequest)
      console.debug('[msalAgent]: after popup, response=', loginResponse)
      const { account } = this.processLoginResponse(loginResponse)
      if (!account) throw new AuthenticationError('No account after login.')
      return false // do not retry
    } catch (e) {
      // handle case where user cancels authentication
      if (e.errorCode === 'access_denied' && e.errorMessage.startsWith('AADB2C90091')) {
        return true // try again
      }

      console.error('[msalAgent]: loginPopup errorCode=', e.errorCode)
      sessionStorage.removeItem('msal.interaction.status')

      if (e instanceof BrowserAuthError) {
        if (e.errorCode === 'interaction_in_progress') throw new AuthenticationError(e)
        if (e.errorCode === 'user_cancelled') return true // try again
        else throw new PopupError(e)
      }

      throw new AuthenticationError(e)
    }
  }

  async loginWithRedirect(options) {
    const loginRequest = {
      ...this.constructLoginRequest(options)
    }

    try {
      console.debug('[msalAgent]: loginRequest=', loginRequest)
      await this.msalClient.loginRedirect(loginRequest)
      console.warn('[msalAgent]: Unexpectedly returning from loginRedirect!')
      return false
    } catch (e) {
      // handle case where user cancels authentication
      if (e.errorCode === 'access_denied' && e.errorMessage.startsWith('AADB2C90091')) {
        return true // try again
      }

      console.error('[msalAgent]: loginRedirect error.', e)
      throw new AuthenticationError(e)
    }
  }

  async loginWithSSO(options) {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-browser-samples/VanillaJSTestApp2.0/app/ssoSilent/auth.js
    const loginRequest = {
      ...this.constructLoginRequest(options)
    }

    try {
      console.log('[msalAgent]: Attempting silent SSO...')
      loginRequest.prompt = 'none'
      const loginResponse = await this.msalClient.ssoSilent(loginRequest)
      this.processLoginResponse(loginResponse)
      return false // do not retry
    } catch (e) {
      // handle case where user cancels authentication
      if (e.errorCode === 'access_denied' && e.errorMessage.startsWith('AADB2C90091')) {
        return true // try again
      }

      if (e instanceof InteractionRequiredAuthError) {
        console.debug('[msalAgent]: Interaction required...')
        loginRequest.prompt = 'select_account'
        const retry = await this.loginWithPopup(loginRequest)
        return retry
      } else {
        console.error('[msalAgent]: SSO login error.', e)
        throw new AuthenticationError(e)
      }
    }
  }

  async logout(options) {
    const logoutBaseRequest = {
      // promptless logout options:
      // account: this.account // msal bug: causes msal.interaction.status in local storage
      // idTokenHint: this.idToken
      logoutHint: this.getUserId()
    }

    try {
      const logoutPopupRequest = {
        mainWindowRedirectUri: options?.redirectURI || `/close`,
        // postLogoutRedirectUri: options?.redirectURI || `/close`
        postLogoutRedirectUri: `/close?reason=logout`
      }
      const logoutRequest = {
        ...logoutBaseRequest,
        ...logoutPopupRequest
      }

      console.debug('[msalAgent]: Logout request=', logoutRequest)
      const logoutResponse = await this.msalClient.logoutPopup(logoutRequest)
      console.debug('[msalAgent]: Logout response=', logoutResponse)
    } catch (e) {
      console.error('[msalAgent]: Logout error.', e)

      if (
        e instanceof BrowserAuthError ||
        e.errorCode === 'popup_window_error' ||
        e.errorCode === 'empty_window_error'
      ) {
        // fallback to redirect logout
        const logoutRedirectRequest = {
          // onRedirectNavigate: (url) => onLogoutRedirect(url)
          postLogoutRedirectUri: options?.redirectURI || `/close`
        }
        const redirectLogoutRequest = {
          ...logoutBaseRequest,
          ...logoutRedirectRequest
        }
        console.debug('[msalAgent]: Redirect Logout request=', redirectLogoutRequest)
        await this.msalClient.logoutRedirect(redirectLogoutRequest)
      }
    } finally {
      this.authenticated = false
      this.setAccount(null)
      this.clearInteractions()
      // this.clearCookies()
    }
  }

  clearInteractions() {
    // clear any pending msal interactions
    sessionStorage.removeItem('msal.interaction.status')
  }

  clearCookies() {
    // expire all msal cookies
    const cookies = document.cookie.split(';')
    console.warn('[msalAgent]: cookies=', cookies)
    cookies.forEach(
      // cookie syntax: <name>=<value>
      (cookie) =>
        (document.cookie = cookie
          // remove spaces
          .replace(/^ +/, '')
          // replace everything after '=' (i.e. entire value of cookie)
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/'))
    )
  }

  onLogoutRedirect(url) {
    console.debug('[msalAgent]: onLogoutRedirect url=', url)
    this.authenticated = false
    this.setAccount(null)
    this.clearCache()
    return true // 'false' restricts to being just a local logout
  }

  isAuthenticated() {
    return this.authenticated
  }

  /*
   * Account Methods
   */

  setAccount(account) {
    this.account = account
    this.msalClient.setActiveAccount(this.account)
  }

  findAccount() {
    const currentAccounts = this.msalClient.getAllAccounts()
    if (!currentAccounts || currentAccounts.length < 1) {
      console.info('[msalAgent]: No active accounts!')
      return null
    } else if (currentAccounts.length === 1) {
      console.info('[msalAgent]: Active account detected!')
      console.info('[msalAgent]: Selecting', currentAccounts[0].username)
      return currentAccounts[0]
    } else if (currentAccounts.length > 1) {
      // TODO: add account chooser code (only required if one allows multiple logins per session)
      console.info('[msalAgent]: Multiple accounts detected!')
      console.warn('[msalAgent]: Accounts=', currentAccounts)
      // FIXME: currently just selecting the most recent account, but this is not ideal if multiple accounts are expected in a session (e.g. shared computer)
      // const selectedAccount = this.selectAccount(currentAccounts)
      const selectedAccount = currentAccounts[currentAccounts.length - 1]
      console.info('[msalAgent]: Selecting', selectedAccount.idTokenClaims?.email)
      return selectedAccount
    }
  }

  selectAccount(currentAccounts) {
    /**
     * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
     * is cached as a new account, which results in more than one account in the cache. Here we make
     * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
     * as this is the default flow the user initially signed-in with.
     */
    const accounts = currentAccounts.filter(
      (account) =>
        account.homeAccountId.toUpperCase().includes(msalConfig.auth.policy.toUpperCase()) &&
        account.idTokenClaims &&
        account.idTokenClaims.iss.toUpperCase().includes(msalConfig.auth.authority.toUpperCase()) &&
        account.idTokenClaims.aud === msalConfig.auth.clientId
    )

    // localAccountId identifies the entity for which the token asserts information.
    if (accounts.every((account) => account.localAccountId === accounts[0].localAccountId)) {
      // All accounts belong to the same user
      return accounts[0]
    } else {
      // Multiple users detected. Logout all to be safe.
      this.logout({})
      return null
    }
  }

  /*
   * User Methods
   */

  getUserId() {
    // userId is case insensitive (in Lumenii and ActiveDirectory)
    return this.account?.username || this.account?.idTokenClaims?.email
  }

  getUserEmail() {
    return this.account?.username || this.account?.idTokenClaims?.email
  }

  getUserDisplayName() {
    return this.account?.name
  }

  getUserUniqueId() {
    return this.account?.localAccountId
  }

  getUserPicture() {
    // FIXME: probably won't work
    return this.account?.picture
  }

  getUserSubject() {
    return this.account?.idTokenClaims?.sub
  }

  /*
   * Token Methods
   */

  async getIdTokenClaims() {
    return this.account?.idTokenClaims
  }

  async acquireTokens(options) {
    // TODO: intermittent error: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/1156
    const tokenRequest = {
      account: this.account,
      scopes: options.scopes || this.scopes,
      forceRefresh: false, // false -> get tokens from cache
      // FIXME: block_iframe_reload error...need to redirect to a non-MSAL route
      redirectUri: `${window.location.origin}/splash`
    }

    try {
      // first, attempt to retrieve tokens silently
      const response = await this.msalClient.acquireTokenSilent(tokenRequest)
      if (response) {
        this.accessToken = response.accessToken
        return this.accessToken
      } else {
        const errorMessage = '[msalAgent]: acquireTokenSilent returned null response.'
        console.error(errorMessage)
        throw new TokenError(errorMessage)
      }
    } catch (e) {
      // next issue a popup - because tokens are expired, password was changed, or account policies were updated
      if (e instanceof InteractionRequiredAuthError) {
        try {
          const response = await this.msalClient.acquireTokenPopup(tokenRequest)
          this.accessToken = response.accessToken
          return this.accessToken
        } catch (e) {
          console.error(`[msalAgent]: Unable to acquire token (code: ${e.errorCode}). ${e}`)
          // finally, if popups are blocked, try a redirect
          if (
            e instanceof BrowserAuthError ||
            e.errorCode === 'popup_window_error' ||
            e.errorCode === 'empty_window_error'
          ) {
            await this.msalClient.acquireTokenRedirect(tokenRequest)
          } else {
            console.error('[msalAgent]: acquireTokenPopup error=', e)
            throw new TokenError(e)
          }
        }
      } else {
        console.error('[msalAgent]: acquireTokenSilent error=', e)
        throw new TokenError(e)
      }
    }
  }
}

export const createMsalAgent = async (options) => {
  const agent = new MsalAgent()
  await agent.initialize(options)
  return agent
}
